// Tiny toast store. Single global registry the App shell reads from.
// Toasts auto-dismiss; the live region is aria-live="polite" per BUILD.md §13.6.
//
// Toasts may carry an action (label + onClick), used for the "Undo"
// affordance on reversible-destructive mutations like project archive.
// Clicking the action also dismisses the toast.

import { useEffect, useState } from 'react';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: number;
  message: string;
  kind: 'info' | 'error' | 'success';
  action?: ToastAction;
}

type Listener = (toasts: Toast[]) => void;
const subs = new Set<Listener>();
let store: Toast[] = [];
let nextId = 1;

function emit() { for (const s of subs) s(store); }

function dismiss(id: number) {
  store = store.filter((t) => t.id !== id);
  emit();
}

export function pushToast(
  message: string,
  kind: Toast['kind'] = 'info',
  ttlMs = 3500,
  action?: ToastAction,
): number {
  const id = nextId++;
  // Wrap the caller's action so it dismisses the toast too.
  const wrappedAction: ToastAction | undefined = action
    ? { label: action.label, onClick: () => { action.onClick(); dismiss(id); } }
    : undefined;
  store = [...store, { id, message, kind, action: wrappedAction }];
  emit();
  setTimeout(() => dismiss(id), ttlMs);
  return id;
}

export function dismissToast(id: number) { dismiss(id); }

export function useToasts(): Toast[] {
  const [state, setState] = useState<Toast[]>(store);
  useEffect(() => {
    subs.add(setState);
    return () => { subs.delete(setState); };
  }, []);
  return state;
}
